/**
 * JWT Token Utilities
 * Handles token parsing, storage, and expiry checking
 */

const TOKEN_KEY = 'jwt_token'
const ROLE_KEY = 'user_role'

/**
 * Parse JWT token to extract payload
 */
export function parseJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    console.error('Failed to parse JWT:', error)
    return null
  }
}

/**
 * Get token expiry timestamp (in seconds)
 */
export function getTokenExpiry(token: string): number | null {
  const payload = parseJWT(token)
  return payload?.exp || null
}

/**
 * Check if token is expiring soon
 * @param token JWT token
 * @param bufferMinutes Minutes before expiry to consider "soon" (default: 10)
 */
export function isTokenExpiringSoon(token: string, bufferMinutes: number = 10): boolean {
  const expiry = getTokenExpiry(token)
  if (!expiry) return true

  const expiryTime = expiry * 1000 // Convert to milliseconds
  const bufferTime = bufferMinutes * 60 * 1000
  const now = Date.now()

  return expiryTime - now < bufferTime
}

/**
 * Check if token is expired
 */
export function isTokenExpired(token: string): boolean {
  const expiry = getTokenExpiry(token)
  if (!expiry) return true

  const expiryTime = expiry * 1000
  return Date.now() >= expiryTime
}

/**
 * Get stored JWT token from localStorage
 */
export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY)
}

/**
 * Store JWT token in localStorage
 */
export function setStoredToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

/**
 * Remove JWT token from localStorage
 */
export function removeStoredToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

/**
 * Get stored role from localStorage
 */
export function getStoredRole(): 'user' | 'admin' | null {
  const role = localStorage.getItem(ROLE_KEY)
  return role === 'user' || role === 'admin' ? role : null
}

/**
 * Store role in localStorage
 */
export function setStoredRole(role: 'user' | 'admin'): void {
  localStorage.setItem(ROLE_KEY, role)
}

/**
 * Remove role from localStorage
 */
export function removeStoredRole(): void {
  localStorage.removeItem(ROLE_KEY)
}

/**
 * Clear all auth-related data from localStorage
 */
export function clearAuthData(): void {
  removeStoredToken()
  removeStoredRole()
  // Also clear any user-specific data
  localStorage.removeItem('user_email')
  localStorage.removeItem('user_id')
}

